import { keepPreviousData, useInfiniteQuery } from '@tanstack/vue-query';
import { onScopeDispose, Ref, ref } from 'vue';
import { createSearchFilter } from '@infuse/e-connector/shared/utils';
import { FilterExpression, Pageable, SearchOptions } from '@infuse/api/common';

export interface PageableResourceOptions {
  queryKey: string;
  searchableFields: string[];
  pageSize: number;
}

export const ECONNECTOR_MAX_PAGE_SIZE = 2000;

export const usePageableResource = <T>(apiFn: (searchOptions: SearchOptions) => Promise<Pageable<T>>, options: PageableResourceOptions) => {
  const searchQuery = ref<string | undefined>();
  const searchOptions: Ref<Omit<SearchOptions, 'page'>> = ref({ size: options.pageSize });

  const query = useInfiniteQuery<Pageable<T>>({
    initialPageParam: 0,
    queryKey: [options.queryKey, searchOptions, searchQuery],
    queryFn: ({ pageParam }) => {
      const searchFilter = createSearchFilter(searchQuery.value, options.searchableFields);
      const filter = searchOptions.value.filter ? searchFilter.and(searchOptions.value.filter as FilterExpression) : searchFilter; // apply to search filter as to not modify searchOptions.value.filter

      return apiFn({ ...searchOptions.value, filter, page: +pageParam });
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage?.pageable?.pageNumber + 1),
    placeholderData: keepPreviousData
  });

  // after scope dispose (component unmount) vue-query unsubscribes its observer,
  // so query.hasNextPage freezes at its last value and can never become false again
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  const fetchAllPages = async () => {
    // ensure first page has loaded
    if (!query.data.value) {
      await query.refetch();
    }

    while (!disposed && query.hasNextPage.value) {
      await query.fetchNextPage({ cancelRefetch: false });
    }
  };

  return {
    ...query,
    searchQuery,
    searchOptions,
    fetchAllPages
  };
};
